import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAllSchemesWithDetails, useDeleteSchemeDetail } from "http-hooks/schemedetail/useSchemeDetail";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { LoadingRow, EmptyRow, Table, THead, TBody, TR, TH, TD, ActionButton } from "components/ui/Table";
import Select from "components/ui/Select";
import SelectVendors from "components/ui/selectors/SelectVendors";
import Modal from "components/ui/modals";
import DeleteConfirmation from "components/ui/DeleteConfirmation";
import useAuth from "hooks/useAuth";

const statusOptions = [
  { value: "", label: "All Status" },
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];

export default function SchemeDetailSelector() {
  const navigate = useNavigate();
  const { agttp } = useAuth();

  const [filters, setFilters] = useState({
    vendor: null,
    ag_status: "",
  });
  const [deleteSchemeId, setDeleteSchemeId] = useState(null);

  const { mutateAsync: deleteSchemeDetail, isPending: isDeleting } = useDeleteSchemeDetail();

  const { data, isLoading } = useAllSchemesWithDetails(
    {
      ...(filters.vendor && { vendor: filters.vendor.ps_Id }),
      ...(filters.ag_status && { ag_status: filters.ag_status }),
      noPagination: true,
    },
    true
  );

  const schemes = data?.data || [];

  const handleEdit = (schemeId) => {
    navigate(`/scheme-detail/edit/${schemeId}`);
  };

  const handleDelete = async () => {
    try {
      await deleteSchemeDetail(deleteSchemeId);
      setDeleteSchemeId(null);
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">
            Scheme Detail
          </h1>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={() => navigate("/scheme-detail")}
            className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Create Scheme Detail
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {agttp === 0 && (
          <SelectVendors
            label="Vendor"
            value={filters.vendor}
            onChange={(value) => setFilters({ ...filters, vendor: value })}
            isClearable={true}
          />
        )}
        <Select
          label="Status"
          options={statusOptions}
          value={statusOptions.find((opt) => opt.value === filters.ag_status)}
          onChange={(selected) =>
            setFilters({ ...filters, ag_status: selected?.value || "" })
          }
          isClearable={false}
        />
      </div>

      {/* Table */}
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <Table>
              <THead>
                <TR>
                  <TH>Scheme Name</TH>
                  {agttp === 0 && <TH>Vendor</TH>}
                  <TH>Date</TH>
                  <TH>Circular Date</TH>
                  <TH>Period</TH>
                  <TH>Details Count</TH>
                  <TH last>
                    <span className="sr-only">Actions</span>
                  </TH>
                </TR>
              </THead>
              <TBody>
                {isLoading ? (
                  <LoadingRow colSpan={agttp === 0 ? 7 : 6} />
                ) : schemes.length === 0 ? (
                  <EmptyRow colSpan={agttp === 0 ? 7 : 6} />
                ) : (
                  schemes.map((scheme) => (
                    <TR key={scheme.schemeid}>
                      <TD>{scheme.sch_name}</TD>
                      {agttp === 0 && (
                        <TD>{scheme.vendorDetail?.name || scheme.vendor}</TD>
                      )}
                      <TD>{new Date(scheme.sch_date).toLocaleDateString()}</TD>
                      <TD>
                        {scheme.circlr_date
                          ? new Date(scheme.circlr_date).toLocaleDateString()
                          : "-"}
                      </TD>
                      <TD>
                        {new Date(scheme.sch_fm).toLocaleDateString()} -{" "}
                        {new Date(scheme.sch_to).toLocaleDateString()}
                      </TD>
                      <TD>{scheme.detailCount || 0}</TD>
                      <TD last>
                        <ActionButton
                          onClick={() => handleEdit(scheme.schemeid)}
                          variant="secondary"
                          title="Edit Details"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </ActionButton>
                        <ActionButton
                          onClick={() => setDeleteSchemeId(scheme.schemeid)}
                          variant="danger"
                          title="Delete"
                          className="mr-0"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </ActionButton>
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </div>
        </div>
      </div>

      {deleteSchemeId && (
        <Modal open={!!deleteSchemeId} setOpen={(val) => !val && setDeleteSchemeId(null)}>
          <DeleteConfirmation
            title="Delete Scheme Detail"
            description="Are you sure you want to delete this scheme detail? All gift items and detail items for this scheme will be permanently removed. This action cannot be undone."
            onDelete={handleDelete}
            onClose={() => setDeleteSchemeId(null)}
            isLoading={isDeleting}
          />
        </Modal>
      )}
    </div>
  );
}
